import { useMemo } from 'react';
import {
  type AssignmentDto,
  daySegments,
  dominantPersonOfDay,
  formatDayLong,
} from '@betreuung/shared';
import { TimeBand } from '@/components/shared/TimeBand';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';
import type { DaySelectionRole } from './useMonthSelection';

export interface DayCellProps {
  dayIndex: number;
  /** Number in the corner — the day of its own month, not of the grid. */
  dayOfMonth: number;
  assignments: readonly AssignmentDto[];
  persons: PersonLookup;
  /** 8px on phones, 10px on desktop. */
  bandHeight: number;
  /** Days of the neighbouring months are dimmed and not selectable. */
  inMonth: boolean;
  isToday: boolean;
  role: DaySelectionRole;
  onTap: (dayIndex: number) => void;
}

/**
 * One day of the month grid: tinted in the colour of whoever covers most of it,
 * with the chronological band along the bottom edge.
 *
 * The whole tile is a single control with a single outcome. It stays a container
 * rather than a bare button because the day number and the time band have to sit
 * above the hit area without intercepting it.
 */
export function DayCell({
  dayIndex,
  dayOfMonth,
  assignments,
  persons,
  bandHeight,
  inMonth,
  isToday,
  role,
  onTap,
}: DayCellProps) {
  const segments = useMemo(() => daySegments(assignments, dayIndex), [assignments, dayIndex]);
  const dominantId = dominantPersonOfDay(segments);
  const owner = dominantId === null ? null : persons.describe(dominantId);

  const dayLabel = formatDayLong(dayIndex);
  const selected = role === 'end';

  return (
    <div
      style={owner ? personVars(owner.colorIndex) : undefined}
      // `touch-manipulation` keeps the second tap of a range from being read as
      // a double-tap zoom; `select-none` keeps it from selecting the day number.
      className={cn(
        'relative box-border h-[50px] touch-manipulation overflow-hidden rounded-cell select-none nav:h-[74px] nav:rounded-[9px]',
        // Exactly one background is chosen here rather than layering `day-end`
        // over `person-tint-cell`: Tailwind emits custom utilities in
        // alphabetical order, so the tint would win over the selection and the
        // cascade would silently decide the outcome.
        // The transparent border keeps filled and tinted cells the same size as
        // the dashed empty ones.
        selected
          ? 'day-end border border-transparent'
          : owner
            ? 'person-tint-cell border border-transparent'
            : 'free-surface',
        role === 'between' && 'day-between',
        // The today ring is a box shadow, so hover must layer with it rather
        // than replace it — otherwise hovering today erased the marker.
        isToday && !selected && 'ring-today',
        inMonth ? null : 'opacity-[.32]',
      )}
    >
      <button
        type="button"
        disabled={!inMonth}
        aria-pressed={selected}
        aria-label={de.a11y.dayCell(dayLabel, owner?.name ?? de.a11y.unassigned)}
        onClick={() => onTap(dayIndex)}
        className={cn(
          'absolute inset-0 z-10 rounded-cell',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
          inMonth && !selected && role !== 'between'
            ? 'nav:hover:shadow-[inset_0_0_0_2px_var(--color-fg-hint)]'
            : null,
        )}
      />

      <span
        className={cn(
          'pointer-events-none absolute top-[5px] left-[7px] z-[5] text-hint font-semibold nav:top-1.5 nav:left-2 nav:text-label',
          selected ? 'text-accent-fg' : 'text-fg-secondary',
        )}
      >
        {dayOfMonth}
      </span>

      <TimeBand
        segments={segments}
        persons={persons}
        height={bandHeight}
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[5]"
      />
    </div>
  );
}
